import { useEffect, useState } from 'react';
import { Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation, withTranslation } from 'react-i18next';
import { BiInfoCircle } from 'react-icons/bi';
import { Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import useBasicGraphCard from '../../../core/hooks/useBasicGraphCard';
import Utils from '../../../core/utilities/Utils';
import ChartMenu from '../../../components/ChartMenu';
import TableViewModal from '../NewDashboard/TableViewModal';
import './ESGAnalyticsLineGraphYearly.scss';

const monthOrder = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const monthToQuarter = { JAN: 'Q1', FEB: 'Q1', MAR: 'Q1', APR: 'Q2', MAY: 'Q2', JUN: 'Q2', JUL: 'Q3', AUG: 'Q3', SEP: 'Q3', OCT: 'Q4', NOV: 'Q4', DEC: 'Q4' };
const quarterOrder = ['Q1', 'Q2', 'Q3', 'Q4'];

const ESGAnalyticsLineGraphYearly = ({ t, dataViewData, configRows, filter, showChartMenu = true, showMaximizeInMenu = true, showViewTableInMenu = true }) => {
  const { i18n } = useTranslation();
  const {
    chartData,
    chartOptions,
    setChartData,
    setChartOptions,
    chartRef
  } = useBasicGraphCard(t);

  const [chartReady, setChartReady] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [tableViewModalOpen, setTableViewModalOpen] = useState(false);
  const [isMaximized, setMaximized] = useState(false);

  useEffect(() => {
    if (dataViewData && configRows) {
      setupLineChartDataByPeriod(dataViewData, configRows, filter);
    } else {
      setChartReady(false);
    }
  }, [dataViewData, configRows, filter, showLabels, i18n.language]);

  const setupLineChartDataByPeriod = (dataViewData, configRows, filter) => {
    if (!dataViewData || typeof dataViewData !== 'object' || !configRows || configRows.length === 0) {
      setChartReady(false);
      return;
    }

    const isMonthlyStructure = Object.keys(dataViewData).some(year => {
      const yearData = dataViewData[year];
      if (yearData && typeof yearData === 'object') {
        return Object.keys(yearData).some(key => monthOrder.includes(key));
      }
      return false;
    });

    const periodType = filter?.periodType || 'YEARLY';
    const useYearlyXAxis = periodType === 'YEARLY';
    const useMonthlyXAxis = periodType === 'MONTHLY';
    const useQuarterlyXAxis = periodType === 'QUARTERLY';

    const totalLabel = t('Total.label') || 'Total';
    const chartItems = [];
    configRows.forEach((row, rowIndex) => {
      if (row.useLevel1Equation && row.level1Equation?.expressionWithId) {
        const name = row.name || `Level1 Equation ${rowIndex + 1}`;
        if (name === totalLabel) return;
        chartItems.push({
          id: row.level1Equation.expressionWithId,
          itemType: 'equation',
          name,
          level: 'level1'
        });
      } else if (row.level1?.id) {
        const name = row.name || row.level1.name || row.level1.dataPointName || row.level1.metricName || `Level1 ${rowIndex + 1}`;
        if (name === totalLabel) return;
        chartItems.push({
          id: row.level1.id,
          itemType: row.level1.itemType || 'datapoint',
          name,
          level: 'level1'
        });
      }
    });

    if (chartItems.length === 0) {
      setChartReady(false);
      return;
    }

    let allPeriodKeys = [];
    const labels = [];

    if (useYearlyXAxis) {
      const allYearsSet = isMonthlyStructure
        ? new Set(
            (() => {
              const set = new Set();
              Object.keys(dataViewData).forEach(year => {
                const yearData = dataViewData[year];
                if (yearData && typeof yearData === 'object') {
                  Object.keys(yearData).forEach(period => {
                    if (monthOrder.includes(period)) set.add(year);
                  });
                }
              });
              return set;
            })()
          )
        : new Set(Object.keys(dataViewData).filter(y => /^\d{4}$/.test(y)));
      const allYears = Array.from(allYearsSet).sort((a, b) => parseInt(a) - parseInt(b));
      allPeriodKeys = allYears;
      allYears.forEach(year => labels.push(year));
    } else if (useMonthlyXAxis) {
      const allMonthsSet = new Set();
      Object.keys(dataViewData).forEach(year => {
        const yearData = dataViewData[year];
        if (yearData && typeof yearData === 'object') {
          if (isMonthlyStructure) {
            Object.keys(yearData).forEach(period => {
              if (monthOrder.includes(period)) allMonthsSet.add(`${year}-${period}`);
            });
          } else {
            Object.keys(yearData).forEach(period => {
              if (period !== 'YEARLY') allMonthsSet.add(`${year}-${period}`);
            });
          }
        }
      });
      allPeriodKeys = Array.from(allMonthsSet).sort((a, b) => {
        const [yearA, periodA] = a.split('-');
        const [yearB, periodB] = b.split('-');
        if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB);
        if (monthOrder.includes(periodA) && monthOrder.includes(periodB)) {
          return monthOrder.indexOf(periodA) - monthOrder.indexOf(periodB);
        }
        return String(periodA).localeCompare(String(periodB));
      });
      allPeriodKeys.forEach(periodKey => {
        const [year, period] = periodKey.split('-');
        if (monthOrder.includes(period)) {
          const monthIndex = monthOrder.indexOf(period);
          const date = new Date(parseInt(year), monthIndex, 1);
          const monthName = new Intl.DateTimeFormat(i18n.language, { month: 'short' }).format(date);
          labels.push(`${monthName} ${year}`);
        } else {
          labels.push(`${period} ${year}`);
        }
      });
    } else if (useQuarterlyXAxis) {
      const allQuarterKeysSet = new Set();
      Object.keys(dataViewData).forEach(year => {
        const yearData = dataViewData[year];
        if (yearData && typeof yearData === 'object') {
          if (isMonthlyStructure) {
            Object.keys(yearData).forEach(period => {
              if (monthOrder.includes(period) && monthToQuarter[period]) {
                allQuarterKeysSet.add(`${year}-${monthToQuarter[period]}`);
              }
            });
          } else {
            Object.keys(yearData).forEach(period => {
              if (period !== 'YEARLY' && quarterOrder.includes(period)) {
                allQuarterKeysSet.add(`${year}-${period}`);
              }
            });
          }
        }
      });
      allPeriodKeys = Array.from(allQuarterKeysSet).sort((a, b) => {
        const [yearA, qA] = a.split('-');
        const [yearB, qB] = b.split('-');
        if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB);
        return quarterOrder.indexOf(qA) - quarterOrder.indexOf(qB);
      });
      allPeriodKeys.forEach(periodKey => {
        const [year, period] = periodKey.split('-');
        labels.push(`${period} ${year}`);
      });
    }

    if (allPeriodKeys.length === 0 || labels.length === 0) {
      setChartReady(false);
      return;
    }

    // Same as "Organisational Breakdown" (renderFirst) table: use metric/datapoint/equation.total only
    const extractValueFromPeriodData = (periodData, item) => {
      if (!periodData || typeof periodData !== 'object') return null;
      if (item.itemType === 'equation' && periodData.equations?.length) {
        const equation = periodData.equations.find(eq =>
          [eq.equation, eq.expressionWithId, eq.id].some(id => String(id) === String(item.id))
        );
        return equation?.total != null ? parseFloat(equation.total) : null;
      }
      if (item.itemType === 'metric' || !item.itemType) {
        const metric = (periodData.metrics || []).find(m =>
          [m.metricId, m.id].some(id => String(id) === String(item.id))
        );
        if (metric?.total != null) return parseFloat(metric.total);
      }
      const datapoint = (periodData.datapoints || []).find(dp =>
        [dp.dataPointId, dp.id].some(id => String(id) === String(item.id))
      );
      return datapoint?.total != null ? parseFloat(datapoint.total) : null;
    };

    const getPeriodDataForKey = (periodKey) => {
      if (useYearlyXAxis) {
        const year = periodKey;
        const yearData = dataViewData[year];
        if (!yearData || typeof yearData !== 'object') return null;
        if (!isMonthlyStructure) {
          return yearData['YEARLY'] || (yearData.metrics || yearData.datapoints ? yearData : null) || Object.values(yearData)[0] || null;
        }
        return null;
      }
      const [y, period] = periodKey.split('-');
      const yd = dataViewData[y];
      return (yd && yd[period]) || null;
    };

    const extractUomFromPeriodData = (periodData, item) => {
      if (!periodData || typeof periodData !== 'object') return '';
      if (item.itemType === 'equation' && periodData.equations?.length) {
        const equation = periodData.equations.find((eq) =>
          [eq.equation, eq.expressionWithId, eq.id].some((id) => id != null && String(id) === String(item.id))
        );
        return (equation?.uom && String(equation.uom).trim()) || '';
      }
      if (item.itemType === 'metric' || item.itemType === 'datapoint') {
        const metric = (periodData.metrics || []).find((m) =>
          [m.metricId, m.id].some((id) => id != null && String(id) === String(item.id))
        );
        if (metric?.uom) return String(metric.uom).trim();
        const datapoint = (periodData.datapoints || []).find((dp) =>
          [dp.dataPointId, dp.id].some((id) => id != null && String(id) === String(item.id))
        );
        return (datapoint?.uom && String(datapoint.uom).trim()) || '';
      }
      const metric = (periodData.metrics || []).find((m) =>
        [m.metricId, m.id].some((id) => id != null && String(id) === String(item.id))
      );
      if (metric?.uom) return String(metric.uom).trim();
      const datapoint = (periodData.datapoints || []).find((dp) =>
        [dp.dataPointId, dp.id].some((id) => id != null && String(id) === String(item.id))
      );
      return (datapoint?.uom && String(datapoint.uom).trim()) || '';
    };

    /** Period payload for UOM when yearly axis aggregates monthly buckets (getPeriodDataForKey is null per year). */
    const getPeriodDataForUomResolution = (periodKey) => {
      const direct = getPeriodDataForKey(periodKey);
      if (direct) return direct;
      if (useYearlyXAxis && isMonthlyStructure) {
        const year = periodKey;
        const yearData = dataViewData[year];
        if (!yearData || typeof yearData !== 'object') return null;
        for (const m of monthOrder) {
          if (yearData[m] && typeof yearData[m] === 'object') {
            return yearData[m];
          }
        }
      }
      return null;
    };

    const getSeriesUomForItem = (item) => {
      const uoms = new Set();
      allPeriodKeys.forEach((periodKey) => {
        const periodData = getPeriodDataForUomResolution(periodKey);
        if (!periodData) return;
        const u = extractUomFromPeriodData(periodData, item);
        if (u) uoms.add(u);
      });
      if (uoms.size === 0) return '';
      return Array.from(uoms).sort().join(' / ');
    };

    const colors = [
      { borderColor: '#3b82f6', backgroundColor: '#3b82f680' },
      { borderColor: '#10b981', backgroundColor: '#10b98180' },
      { borderColor: '#f59e0b', backgroundColor: '#f59e0b80' },
      { borderColor: '#ef4444', backgroundColor: '#ef444480' },
      { borderColor: '#8b5cf6', backgroundColor: '#8b5cf680' },
      { borderColor: '#06b6d4', backgroundColor: '#06b6d480' },
      { borderColor: '#ec4899', backgroundColor: '#ec489980' },
      { borderColor: '#14b8a6', backgroundColor: '#14b8a680' },
    ];

    const datasets = [];
    let colorIndex = 0;

    chartItems.forEach((item) => {
      const data = [];

      if (useYearlyXAxis && isMonthlyStructure) {
        allPeriodKeys.forEach(year => {
          const yearData = dataViewData[year];
          let sum = 0;
          let hasAny = false;
          if (yearData && typeof yearData === 'object') {
            Object.keys(yearData).filter(p => monthOrder.includes(p)).forEach(m => {
              const periodData = yearData[m];
              const v = extractValueFromPeriodData(periodData, item);
              if (v != null) {
                sum += parseFloat(v) || 0;
                hasAny = true;
              }
            });
          }
          data.push(hasAny ? sum : 0);
        });
      } else if (useQuarterlyXAxis && isMonthlyStructure) {
        allPeriodKeys.forEach(periodKey => {
          const [year, quarter] = periodKey.split('-');
          const yearData = dataViewData[year];
          let sum = 0;
          let hasAny = false;
          if (yearData && typeof yearData === 'object' && quarterOrder.includes(quarter)) {
            const monthsInQuarter = Object.keys(monthToQuarter).filter(m => monthToQuarter[m] === quarter);
            monthsInQuarter.forEach(m => {
              const periodData = yearData[m];
              const v = extractValueFromPeriodData(periodData, item);
              if (v != null) {
                sum += parseFloat(v) || 0;
                hasAny = true;
              }
            });
          }
          data.push(hasAny ? sum : 0);
        });
      } else {
        allPeriodKeys.forEach(periodKey => {
          const periodData = getPeriodDataForKey(periodKey);
          const value = extractValueFromPeriodData(periodData, item);
          data.push(value != null ? parseFloat(value) || 0 : 0);
        });
      }

      const hasData = data.some(v => v != null && Number(v) !== 0);
      if (!hasData) return;

      const color = colors[colorIndex % colors.length];
      datasets.push({
        label: item.name,
        uom: getSeriesUomForItem(item),
        borderColor: color.borderColor,
        backgroundColor: color.backgroundColor,
        data,
        tension: 0.4,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
      });
      colorIndex++;
    });

    if (datasets.length === 0) {
      setChartReady(false);
      return;
    }

    const yAxisUomTitle = (() => {
      const uoms = new Set();
      allPeriodKeys.forEach((periodKey) => {
        const periodData = getPeriodDataForUomResolution(periodKey);
        if (!periodData) return;
        chartItems.forEach((item) => {
          const u = extractUomFromPeriodData(periodData, item);
          if (u) uoms.add(u);
        });
      });
      if (uoms.size === 0) return '';
      return Array.from(uoms).sort().join(' / ');
    })();

    const labelCount = labels.length;
    const cOption = {
      layout: {
        padding: {
          top: 24,
          bottom: 5,
          left: 16,
          right: 100,
        },
      },
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          align: 'start',
          labels: {
            font: { size: 12 },
            usePointStyle: true,
            padding: 15
          },
          display: true
        },
        tooltip: {
          callbacks: {
            label: (tooltipItem) => {
              const value = tooltipItem?.dataset?.data[tooltipItem.dataIndex] ?? 0;
              const uom = tooltipItem.dataset.uom;
              const num = Utils.formattedNumber(value);
              const suffix = uom ? ` ${uom}` : '';
              return ` ${tooltipItem.dataset.label}: ${num}${suffix}`;
            }
          }
        },
        datalabels: {
          display: showLabels,
          color: '#374151',
          font: { size: 11, weight: '600' },
          anchor: 'center',
          align: (ctx) => (ctx.dataIndex === labelCount - 1 ? 'left' : 'top'),
          offset: (ctx) => (ctx.dataIndex === labelCount - 1 ? 6 : 4),
          clip: false,
          formatter: (value) => (value === 0 ? '' : Utils.formattedNumber(value)),
        },
      },
      scales: {
        x: {
          offset: true,
          ticks: {
            font: { size: 11 },
            maxRotation: 45,
            minRotation: 45
          },
          grid: {
            display: true,
            color: 'rgba(200, 200, 200, 0.2)'
          }
        },
        y: {
          beginAtZero: true,
          title: {
            display: Boolean(yAxisUomTitle),
            text: yAxisUomTitle,
            font: { size: 11 },
            color: '#64748b',
          },
          ticks: {
            font: { size: 11 },
            callback: function(value) {
              return Utils.formattedNumber(value);
            }
          },
          grid: {
            display: true,
            color: 'rgba(200, 200, 200, 0.2)'
          }
        }
      },
    };

    setChartOptions(cOption);
    setChartData({
      labels,
      datasets,
    });
    setChartReady(true);
  };

  const handleToggleLabels = () => {
    setShowLabels(!showLabels);
  };

  const handleMaximize = () => {
    setMaximized(!isMaximized);
  };

  const handleViewTable = () => {
    setTableViewModalOpen(true);
  };

  const handleCloseTableViewModal = () => {
    setTableViewModalOpen(false);
  };

  const handleDownload = () => {
    if (chartRef?.current) {
      const canvas = chartRef.current.canvas;
      const ctx = canvas.getContext('2d');
      ctx.save();
      const headingText = t('ESGAnalytics.comparison.label') || 'Organisational Breakdown';
      const headingHeight = 56;
      const downloadCanvas = document.createElement('canvas');
      const dCtx = downloadCanvas.getContext('2d');
      downloadCanvas.width = canvas.width;
      downloadCanvas.height = canvas.height + headingHeight;
      dCtx.fillStyle = '#ffffff';
      dCtx.fillRect(0, 0, downloadCanvas.width, downloadCanvas.height);
      dCtx.fillStyle = '#1e293b';
      dCtx.font = '700 24px Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      dCtx.textBaseline = 'middle';
      dCtx.textAlign = 'center';
      dCtx.fillText(headingText, downloadCanvas.width / 2, headingHeight / 2);
      dCtx.drawImage(canvas, 0, headingHeight, canvas.width, canvas.height);
      const link = document.createElement('a');
      link.href = downloadCanvas.toDataURL('image/jpeg', 0.9);
      link.download = `${t("ESGAnalytics.comparison.label") || 'organisational-breakdown'}.jpg`;
      link.click();
      ctx.restore();
    }
  };

  if (!dataViewData || !chartReady) {
    return null;
  }

  return (
    <>
      <Card className="esg-analytics-line-graph-yearly-card mb-3 pt-0">
        <Card.Header className="d-flex justify-content-between align-items-center border-0 pb-0">
          <h5 className="mb-0 d-flex align-items-center gap-2">
            {t("ESGAnalytics.comparison.label")}
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="esg-comparison-info-tooltip">
                  {t('ESGAnalytics.comparisonInfo.label') || 'Shows the contribution of individual units, branches, or entities to the total value'}
                </Tooltip>
              }
            >
              <span className="esg-line-graph-yearly-info-icon" aria-label={t('ESGAnalytics.comparisonInfo.label') || 'Shows the contribution of individual units, branches, or entities to the total value'}>
                <BiInfoCircle size={16} />
              </span>
            </OverlayTrigger>
          </h5>
          {showChartMenu && (
            <ChartMenu
              chartId="esg-analytics-yearly-comparison-chart"
              onToggleLabels={handleToggleLabels}
              onMaximize={handleMaximize}
              onViewTable={handleViewTable}
              onDownload={handleDownload}
              showLabels={showLabels}
              isMaximized={isMaximized}
              showTableView={tableViewModalOpen}
              chartType="LINE"
              showGetInfo={false}
              showToggleLabels={true}
              showToggleView={false}
              showMaximize={showMaximizeInMenu}
              showDataSources={false}
              showViewTable={showViewTableInMenu}
              showDownload={true}
              showDeactivate={false}
            />
          )}
        </Card.Header>
        <Card.Body>
          <div className="line-graph-yearly-container">
            <Line data={chartData} options={chartOptions} ref={chartRef} plugins={[ChartDataLabels]} />
          </div>
        </Card.Body>
      </Card>

      {showChartMenu && (
        <TableViewModal
          open={tableViewModalOpen}
          onClose={handleCloseTableViewModal}
          chartData={chartData}
          chartConfig={{ type: 'LINE' }}
          title={t("ESGAnalytics.comparison.label") || "Organisational Breakdown"}
        />
      )}
    </>
  );
};

export default withTranslation()(ESGAnalyticsLineGraphYearly);
